/**
 * /app/salon/comanda — Pedido del mesero + ver/imprimir órdenes de mesa.
 * Search: ?tableId=...
 * UX: tactil-first (categorías grandes, ver detalle de orden, layout denso).
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ClinicalPageShell } from "@/components/clinical";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Eye,
  Minus,
  Plus,
  Printer,
  QrCode,
  ShoppingBag,
  Trash2,
  CheckCircle2,
  UtensilsCrossed,
} from "lucide-react";
import { CrudShellHeader, Loading } from "@/components/ds";
import { apiGetJson, apiSendJson } from "@/lib/sm-api";
import { tryHipRoleAction } from "@/lib/hardware/client";
import { useWorkspaceTenantId } from "@/hooks/use-workspace-tenant-id";
import { PrintOrderDialog } from "@/components/orders/PrintOrderDialog";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { useOperationalMode, type OperationalShortcut } from "@/hooks/use-operational-mode";
import { OperationalModeShell, OperationalModeToggle } from "@/components/operational-mode/OperationalModeShell";
import { OPERATIONAL_HOST_LAYOUT } from "@/lib/operational-mode-classes";

export const Route = createFileRoute("/app/salon/comanda")({
  component: SalonComandaPage,
  validateSearch: (s: Record<string, unknown>) => ({
    tableId: String(s.tableId ?? ""),
  }),
  head: () => ({ meta: [{ title: "Comanda de mesa — Salón" }] }),
});

interface Producto {
  id: string;
  nombre: string;
  precio: number;
  imagenUrl: string | null;
  categoriaId: string | null;
}

interface Categoria {
  id: string;
  nombre: string;
}

interface CartLine {
  productId: string;
  nombre: string;
  precio: number;
  qty: number;
}

interface OrderItem {
  nombre: string;
  qty: number;
  precioUnitario: number;
  precioTotal: number;
  unidad: string;
}

interface SessionOrder {
  id: string;
  orderNumber: number | null;
  status: string;
  total: number;
  kitchenPrintedAt: string | null;
  kitchenPrintCount: number;
  createdAt: string;
  notes: string | null;
  fromQr?: boolean;
  items: OrderItem[];
}

const pesos = (n: number) =>
  new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 }).format(n);

function OrderCard({
  order,
  expanded,
  onToggle,
  onPrint,
}: {
  order: SessionOrder;
  expanded: boolean;
  onToggle: () => void;
  onPrint: () => void;
}) {
  const needsPrint = order.kitchenPrintCount === 0;
  return (
    <li
      className={cn(
        "rounded-xl border overflow-hidden",
        needsPrint
          ? "border-amber-500/40 bg-amber-500/5"
          : "border-border/50 bg-card/30",
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center gap-2 px-3 py-3 text-left min-h-12"
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="text-sm font-semibold tabular-nums">
              #{order.orderNumber ?? order.id.slice(-6)}
            </span>
            <span className="text-sm font-bold tabular-nums text-primary">{pesos(order.total)}</span>
            {order.fromQr && (
              <Badge variant="outline" className="h-5 gap-0.5 text-[10px] border-sky-500/40 text-sky-300">
                <QrCode className="h-3 w-3" /> QR
              </Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <Badge variant="outline" className="h-5 text-[10px]">
              {order.status}
            </Badge>
            {needsPrint ? (
              <span className="text-[11px] font-medium text-amber-400">Por imprimir</span>
            ) : (
              <span className="flex items-center gap-0.5 text-[11px] text-green-400">
                <CheckCircle2 className="h-3 w-3" /> Impresa ×{order.kitchenPrintCount}
              </span>
            )}
            <span className="text-[11px] text-muted-foreground">
              {order.items.length} ítem{order.items.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
        {expanded ? (
          <ChevronUp className="h-5 w-5 shrink-0 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-5 w-5 shrink-0 text-muted-foreground" />
        )}
      </button>

      {expanded && (
        <div className="border-t border-border/40 px-3 pb-3 pt-2 space-y-3">
          {order.items.length === 0 ? (
            <p className="text-xs text-muted-foreground">Sin detalle de ítems.</p>
          ) : (
            <ul className="space-y-1.5">
              {order.items.map((it, idx) => (
                <li key={`${order.id}-${idx}`} className="flex items-start justify-between gap-2 text-sm">
                  <span className="min-w-0">
                    <span className="font-medium tabular-nums text-primary">{it.qty}×</span>{" "}
                    <span>{it.nombre}</span>
                  </span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">{pesos(it.precioTotal)}</span>
                </li>
              ))}
            </ul>
          )}
          {order.notes && (
            <p className="rounded-lg bg-card px-2.5 py-2 text-xs text-muted-foreground">
              {order.notes}
            </p>
          )}
          <div className="flex gap-2">
            <Button type="button" size="sm" className="h-11 flex-1 text-sm" onClick={onPrint}>
              <Printer className="mr-1.5 h-4 w-4" />
              Imprimir comanda
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

function SalonComandaPage() {
  const { tableId } = Route.useSearch();
  const tid = useWorkspaceTenantId();

  const [tableName, setTableName] = useState("Mesa");
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [products, setProducts] = useState<Producto[]>([]);
  const [categorias, setCategorias] = useState<Categoria[]>([]);
  const [catActiva, setCatActiva] = useState<string>("all");
  const [orders, setOrders] = useState<SessionOrder[]>([]);
  const [cart, setCart] = useState<CartLine[]>([]);
  const [search, setSearch] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [printOrderId, setPrintOrderId] = useState<string | null>(null);
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [cartOpen, setCartOpen] = useState(false);
  const [ordersOpen, setOrdersOpen] = useState(false);

  const load = useCallback(async () => {
    if (!tid || !tableId) return;
    setLoading(true);
    try {
      const [tablesR, ordersR, productsR, catsR] = await Promise.all([
        apiGetJson<{ tables?: Array<{ id: string; nombre: string }> }>(`/api/tables`, tid),
        apiGetJson<{
          session?: { id: string } | null;
          orders?: SessionOrder[];
        }>(`/api/table-sessions?tableId=${encodeURIComponent(tableId)}&action=orders`, tid),
        // Sin `limit`: catálogo completo, sin tope de 200 — la comanda resuelve
        // el buscador de productos contra este snapshot en memoria, igual que el POS.
        apiGetJson<Array<Record<string, unknown>> | { items?: Array<Record<string, unknown>>; products?: Array<Record<string, unknown>> }>(
          `/api/products`,
          tid,
        ),
        apiGetJson<Array<Record<string, unknown>> | { categories?: Array<Record<string, unknown>> }>(
          `/api/categories`,
          tid,
        ),
      ]);
      const tbl = (tablesR.tables ?? []).find((x) => x.id === tableId);
      if (tbl) setTableName(tbl.nombre);
      setSessionId(ordersR.session?.id ?? null);
      const nextOrders = (ordersR.orders ?? []).map((o) => ({
        ...o,
        items: o.items ?? [],
      }));
      setOrders(nextOrders);
      if (nextOrders.length > 0) {
        const firstPending = nextOrders.find((o) => o.kitchenPrintCount === 0) ?? nextOrders[0];
        setExpandedOrderId((prev) => prev ?? firstPending.id);
      }

      const raw = Array.isArray(productsR) ? productsR : (productsR.items ?? productsR.products ?? []);
      setProducts(
        raw.map((p) => ({
          id: String(p.id),
          nombre: String(p.name ?? p.nombre ?? "Producto"),
          precio: Number(p.precioUnitario ?? p.precio_unitario ?? p.price ?? 0),
          imagenUrl: (p.imagenUrl as string | null) ?? (p.imageUrl as string | null) ?? null,
          categoriaId: (p.categoryId ?? p.categoriaId ?? p.category_id ?? null) as string | null,
        })),
      );
      const rawCats = Array.isArray(catsR) ? catsR : (catsR.categories ?? []);
      setCategorias(
        rawCats.map((c) => ({
          id: String(c.id),
          nombre: String(c.nombre ?? c.name ?? ""),
        })),
      );
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al cargar comanda");
    } finally {
      setLoading(false);
    }
  }, [tid, tableId]);

  useEffect(() => {
    void load();
  }, [load]);

  // SCRUM-83: la comanda debe reflejar cambios reales sin que el mesero
  // tenga que recargar a mano (otra terminal marca la orden como impresa,
  // llega un pedido QR nuevo, etc.). Solo refresca sesión/órdenes — nunca
  // catálogo/categorías ni el carrito en armado (ítems todavía sin enviar).
  const refreshOrders = useCallback(async () => {
    if (!tid || !tableId) return;
    try {
      const ordersR = await apiGetJson<{
        session?: { id: string } | null;
        orders?: SessionOrder[];
      }>(`/api/table-sessions?tableId=${encodeURIComponent(tableId)}&action=orders`, tid);
      setSessionId(ordersR.session?.id ?? null);
      setOrders((ordersR.orders ?? []).map((o) => ({ ...o, items: o.items ?? [] })));
    } catch {
      /* poll silencioso — un corte de red pasajero no debe interrumpir al mesero */
    }
  }, [tid, tableId]);

  useEffect(() => {
    if (!tid || !tableId) return;
    const id = window.setInterval(() => void refreshOrders(), 15000);
    return () => window.clearInterval(id);
  }, [tid, tableId, refreshOrders]);

  const addToCart = (p: Producto) => {
    setCart((prev) => {
      const existing = prev.find((l) => l.productId === p.id);
      if (existing) {
        return prev.map((l) => (l.productId === p.id ? { ...l, qty: l.qty + 1 } : l));
      }
      return [...prev, { productId: p.id, nombre: p.nombre, precio: p.precio, qty: 1 }];
    });
  };

  const updateQty = (productId: string, delta: number) => {
    setCart((prev) =>
      prev
        .map((l) => (l.productId === productId ? { ...l, qty: l.qty + delta } : l))
        .filter((l) => l.qty > 0),
    );
  };

  const subtotal = cart.reduce((s, l) => s + l.precio * l.qty, 0);
  const cartCount = cart.reduce((s, l) => s + l.qty, 0);
  const pendingKitchen = orders.filter((o) => o.kitchenPrintCount === 0).length;

  const filtered = useMemo(
    () =>
      products.filter((p) => {
        const matchCat = catActiva === "all" || p.categoriaId === catActiva;
        const matchSearch = !search || p.nombre.toLowerCase().includes(search.toLowerCase());
        return matchCat && matchSearch && p.precio > 0;
      }),
    [products, catActiva, search],
  );

  const ensureSession = async (): Promise<string | null> => {
    if (sessionId) return sessionId;
    if (!tid || !tableId) return null;
    const r = (await apiSendJson(
      `/api/table-sessions`,
      "POST",
      { tableId, comensales: 2 },
      tid,
    )) as { session?: { id: string }; error?: string };
    if (r.error || !r.session) {
      toast.error(r.error ?? "No se pudo abrir la mesa");
      return null;
    }
    setSessionId(r.session.id);
    return r.session.id;
  };

  const handleSendOrder = async () => {
    if (!tid || cart.length === 0) return;
    setSending(true);
    try {
      const sid = await ensureSession();
      if (!sid) return;
      const r = (await apiSendJson(
        `/api/table-sessions`,
        "POST",
        {
          action: "order",
          sessionId: sid,
          tableId,
          notes: notes.trim() || undefined,
          items: cart.map((l) => ({
            productId: l.productId,
            nombre: l.nombre,
            qty: l.qty,
            precioUnitario: l.precio,
          })),
        },
        tid,
      )) as { order?: { id: string }; error?: string };
      if (r.error || !r.order) {
        toast.error(r.error ?? "No se pudo crear el pedido");
        return;
      }
      toast.success("Pedido enviado a cocina");
      // KDS HIP: si hay display asignado, empujar resumen; si no, no-op (comanda impresa / manual).
      void tryHipRoleAction(tid, "kitchenDisplay", "displayMessage", {
        lines: [
          `Pedido ${r.order.id.slice(-6)}`,
          ...cart.map((l) => `${l.qty}× ${l.nombre}`).slice(0, 8),
          notes.trim() ? `Nota: ${notes.trim()}` : "",
        ].filter(Boolean),
      });
      setCart([]);
      setNotes("");
      setCartOpen(false);
      // SCRUM-83: refresh liviano (no `load()` completo) — evita el flash a
      // pantalla de carga que desmontaba el modo operativo justo al enviar
      // un pedido, la acción más frecuente de esta pantalla.
      await refreshOrders();
      setExpandedOrderId(r.order.id);
      setPrintOrderId(r.order.id);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Error al enviar pedido");
    } finally {
      setSending(false);
    }
  };

  // ── SCRUM-83: modo operativo (fullscreen + atajos) ──
  const searchInputRef = useRef<HTMLInputElement>(null);
  const comandaShortcuts = useMemo<OperationalShortcut[]>(
    () => [
      {
        id: "focus-search",
        keys: "F2",
        label: "Buscar producto",
        description: "Enfoca el buscador del catálogo",
        handler: () => {
          searchInputRef.current?.focus();
          searchInputRef.current?.select();
        },
      },
      {
        id: "enviar-cocina",
        keys: "F9",
        label: "Enviar a cocina",
        description: "Envía el pedido actual con el detalle armado",
        handler: () => void handleSendOrder(),
        disabled: cart.length === 0 || sending,
      },
    ],
    [cart.length, sending],
  );
  const operationalMode = useOperationalMode({
    surfaceId: "salon.comanda",
    tenantId: tid ?? null,
    shortcuts: comandaShortcuts,
  });

  const cartPanel = (
    <div className="flex h-full flex-col gap-3">
      {cart.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Tocá productos para armar el pedido</p>
      ) : (
        <ul className="max-h-[40vh] space-y-2 overflow-y-auto lg:max-h-none lg:flex-1">
          {cart.map((l) => (
            <li key={l.productId} className="flex items-center gap-2 rounded-lg border border-border/40 px-2 py-2">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{l.nombre}</p>
                <p className="text-xs tabular-nums text-muted-foreground">{pesos(l.precio)}</p>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  className="h-10 w-10 p-0"
                  onClick={() => updateQty(l.productId, -1)}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="w-6 text-center text-sm font-semibold tabular-nums">{l.qty}</span>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-10 w-10 p-0"
                  onClick={() => updateQty(l.productId, 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-10 w-10 p-0 text-red-400"
                  onClick={() => setCart((c) => c.filter((x) => x.productId !== l.productId))}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notas para cocina (opcional)"
        rows={2}
        className="w-full resize-none rounded-xl border border-border/60 bg-card px-3 py-2.5 text-sm"
      />
      <div className="flex items-center justify-between gap-3">
        <p className="text-lg font-bold tabular-nums">{pesos(subtotal)}</p>
        <Button
          className="h-12 min-w-[9rem] px-5 text-sm"
          disabled={sending || cart.length === 0}
          onClick={() => void handleSendOrder()}
        >
          {sending ? "Enviando…" : "Enviar a cocina"}
        </Button>
      </div>
    </div>
  );

  const ordersPanel = (
    <div className="space-y-2">
      {orders.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">Todavía no hay pedidos en esta mesa</p>
      ) : (
        <ul className="space-y-2">
          {orders.map((o) => (
            <OrderCard
              key={o.id}
              order={o}
              expanded={expandedOrderId === o.id}
              onToggle={() => setExpandedOrderId((id) => (id === o.id ? null : o.id))}
              onPrint={() => setPrintOrderId(o.id)}
            />
          ))}
        </ul>
      )}
    </div>
  );

  if (!tableId) {
    return (
      <ClinicalPageShell>
        <p className="py-10 text-center text-sm text-muted-foreground">Falta tableId.</p>
        <div className="text-center">
          <Button asChild variant="outline" size="sm">
            <Link to="/app/salon" search={{} as never}>Volver al salón</Link>
          </Button>
        </div>
      </ClinicalPageShell>
    );
  }

  if (loading) {
    return (
      <div>
        <CrudShellHeader title={`Comanda · ${tableName}`} subtitle="Tomá el pedido y revisá lo enviado a cocina" />
        <Loading variant="list" rows={4} />
      </div>
    );
  }

  return (
    <OperationalModeShell mode={operationalMode} className={`${OPERATIONAL_HOST_LAYOUT} pb-24 lg:pb-4`}>
      <CrudShellHeader
        title={`Comanda · ${tableName}`}
        subtitle="Tomá el pedido, mirá el detalle e imprimí comanda"
        actions={<OperationalModeToggle mode={operationalMode} />}
      />
      <ClinicalPageShell
        offset="none"
        maxWidth={false}
        className="!px-3 sm:!px-4 lg:!px-5 min-h-0 flex-1 space-y-3 overflow-y-auto pb-6"
      >
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <Link
            to="/app/salon"
            search={{} as never}
            className="inline-flex min-h-11 items-center gap-1.5 rounded-xl border border-border/50 px-3 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-4 w-4" />
            Salón
          </Link>
          {pendingKitchen > 0 && (
            <button
              type="button"
              onClick={() => setOrdersOpen(true)}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-amber-500/40 bg-amber-500/10 px-3 text-sm font-medium text-amber-300 lg:hidden"
            >
              <Eye className="h-4 w-4" />
              {pendingKitchen} por atender
            </button>
          )}
          {orders.length > 0 && (
            <button
              type="button"
              onClick={() => setOrdersOpen(true)}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border border-border/50 px-3 text-sm lg:hidden"
            >
              Ver órdenes ({orders.length})
            </button>
          )}
        </div>

        <div className="grid gap-3 lg:grid-cols-[14rem_minmax(0,1fr)_26rem] xl:grid-cols-[15rem_minmax(0,1fr)_28rem] lg:items-stretch">
          {/* Categorías — táctiles */}
          <aside className="rounded-xl border border-border/40 bg-card/40 p-2 lg:max-h-[calc(100vh-9rem)] lg:overflow-y-auto">
            <p className="mb-2 px-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Categorías
            </p>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-1">
              <button
                type="button"
                onClick={() => setCatActiva("all")}
                className={cn(
                  "min-h-12 rounded-xl border px-3 py-2.5 text-left text-sm font-medium transition-colors",
                  catActiva === "all"
                    ? "border-primary bg-primary/15 text-primary"
                    : "border-border/50 text-foreground/80 hover:border-primary/30",
                )}
              >
                Todos
              </button>
              {categorias.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => setCatActiva(c.id)}
                  className={cn(
                    "min-h-12 rounded-xl border px-3 py-2.5 text-left text-sm font-medium leading-snug transition-colors",
                    catActiva === c.id
                      ? "border-primary bg-primary/15 text-primary"
                      : "border-border/50 text-foreground/80 hover:border-primary/30",
                  )}
                >
                  {c.nombre}
                </button>
              ))}
            </div>
          </aside>

          {/* Catálogo */}
          <section className="flex min-h-0 flex-col overflow-hidden rounded-xl border border-border/40 bg-card/40">
            <div className="border-b border-border/40 p-3">
              <input
                ref={searchInputRef}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Buscar producto…"
                className="h-12 w-full rounded-xl border border-border/60 bg-card px-3 text-sm outline-none focus:border-primary/50"
              />
            </div>
            <div className="max-h-[58vh] flex-1 overflow-y-auto p-2 sm:p-3 lg:max-h-[calc(100vh-9rem)]">
              {filtered.length === 0 ? (
                <p className="py-12 text-center text-sm text-muted-foreground">
                  {search || catActiva !== "all"
                    ? "Sin resultados para tu búsqueda."
                    : "Sin productos. Cargá el menú en Catálogo."}
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                  {filtered.map((p) => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => addToCart(p)}
                      className="group flex min-h-[7.5rem] flex-col overflow-hidden rounded-xl border border-border/60 bg-card text-left transition-all active:scale-[0.98] hover:border-primary/40"
                    >
                      <div className="relative h-16 w-full overflow-hidden bg-card sm:h-20">
                        {p.imagenUrl ? (
                          <img src={p.imagenUrl} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full items-center justify-center text-muted-foreground/25">
                            <UtensilsCrossed className="h-7 w-7" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col p-2.5">
                        <p className="line-clamp-2 text-sm font-medium leading-tight">{p.nombre}</p>
                        <p className="mt-auto pt-1 text-base font-bold tabular-nums text-primary">
                          {pesos(p.precio)}
                        </p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Desktop: ticket + órdenes */}
          <aside className="hidden lg:flex lg:max-h-[calc(100vh-9rem)] lg:flex-col lg:gap-3 lg:overflow-y-auto">
            <div className="rounded-xl border border-border/40 bg-card/40 p-3">
              <div className="mb-3 flex items-center justify-between">
                <h2 className="text-sm font-semibold">Pedido actual</h2>
                <ShoppingBag className="h-4 w-4 text-primary" />
              </div>
              {cartPanel}
            </div>
            <div className="rounded-xl border border-border/40 bg-card/40 p-3">
              <div className="mb-3 flex items-center justify-between gap-2">
                <h2 className="text-sm font-semibold">Órdenes de esta mesa</h2>
                {pendingKitchen > 0 && (
                  <Badge className="bg-amber-500/20 text-amber-300 hover:bg-amber-500/20">
                    {pendingKitchen} por atender
                  </Badge>
                )}
              </div>
              {ordersPanel}
            </div>
          </aside>
        </div>

        {/* Mobile sticky cart */}
        <div className="fixed inset-x-0 bottom-0 z-40 border-t border-border/50 bg-card/95 p-3 backdrop-blur lg:hidden">
          <div className="mx-auto flex max-w-lg gap-2">
            <Button
              type="button"
              variant="outline"
              className="h-12 flex-1 text-sm"
              onClick={() => setOrdersOpen(true)}
            >
              <Eye className="mr-1.5 h-4 w-4" />
              Órdenes
              {pendingKitchen > 0 && (
                <span className="ml-1.5 rounded-full bg-amber-500/20 px-1.5 text-xs text-amber-300">
                  {pendingKitchen}
                </span>
              )}
            </Button>
            <Button type="button" className="h-12 flex-[1.4] text-sm" onClick={() => setCartOpen(true)}>
              <ShoppingBag className="mr-1.5 h-4 w-4" />
              Carrito {cartCount > 0 ? `(${cartCount})` : ""} · {pesos(subtotal)}
            </Button>
          </div>
        </div>

        <Sheet open={cartOpen} onOpenChange={setCartOpen}>
          <SheetContent side="bottom" className="max-h-[85vh] rounded-t-2xl">
            <SheetHeader>
              <SheetTitle>Pedido actual</SheetTitle>
            </SheetHeader>
            <div className="mt-4">{cartPanel}</div>
          </SheetContent>
        </Sheet>

        <Sheet open={ordersOpen} onOpenChange={setOrdersOpen}>
          <SheetContent side="bottom" className="max-h-[85vh] overflow-y-auto rounded-t-2xl">
            <SheetHeader>
              <SheetTitle>Órdenes · {tableName}</SheetTitle>
            </SheetHeader>
            <div className="mt-4">{ordersPanel}</div>
          </SheetContent>
        </Sheet>

        {printOrderId && tid && (
          <PrintOrderDialog
            open={!!printOrderId}
            onOpenChange={(v) => {
              if (!v) setPrintOrderId(null);
            }}
            orderId={printOrderId}
            type="kitchen"
            tid={tid}
            onPrinted={() => {
              void load();
            }}
          />
        )}
      </ClinicalPageShell>
    </OperationalModeShell>
  );
}
